// Pure, dependency-free helpers for tagging chunk-load telemetry in Sentry's beforeSend.
// Kept out of sentry.tsx so they are unit-testable without initialising Sentry.

export type ChunkOutcome = 'recovered' | 'hard_failure' | 'transient';
export type Engine = 'webkit' | 'chromium' | 'firefox' | 'unknown';

// Markers covering both our own logEvent messages (DynamicImportRetry / ChunkErrorRecovery)
// and the raw per-engine browser errors captured via captureException.
const CHUNK_MARKERS =
  /chunk loading|asset retry loading|asset loading exception|retry attempts exhausted|reload (?:already in progress|did not complete)|forcing page reload|module path for retry|failed to fetch dynamically imported|error loading dynamically imported|importing a module script failed/i;

/**
 * Classify a chunk-related event by outcome, or return null if the text is not chunk-related.
 * - recovered    : a retry eventually succeeded ("Asset retry loading successful")
 * - hard_failure : the user was sent to the error page (post-reload failure or stalled reload)
 * - transient    : any other chunk signal (first error, retrying, exhausted, reload queued)
 */
export function classifyChunkEvent(text: string | undefined | null): ChunkOutcome | null {
  if (!text || !CHUNK_MARKERS.test(text)) return null;
  if (/asset retry loading successful/i.test(text)) return 'recovered';
  if (/redirecting to error page/i.test(text)) return 'hard_failure';
  return 'transient';
}

/**
 * Best-effort rendering-engine detection. All iOS browsers are WebKit regardless of brand
 * (Chrome = CriOS, Firefox = FxiOS, Edge = EdgiOS), so those are checked first. UA sniffing
 * drifts — unrecognised strings bucket to 'unknown' rather than being mislabelled.
 */
export function detectEngine(
  ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent
): Engine {
  if (!ua) return 'unknown';
  if (/crios|fxios|edgios/i.test(ua)) return 'webkit';
  if (/edg\//i.test(ua) || /chrome|chromium/i.test(ua)) return 'chromium';
  if (/firefox/i.test(ua)) return 'firefox';
  if (/safari|applewebkit/i.test(ua)) return 'webkit';
  return 'unknown';
}
